import 'vite/modulepreload-polyfill' // eslint-disable-line

import { createRoot } from 'react-dom/client'
import { ApolloProvider } from '@apollo/client/react'
import * as Sentry from '@sentry/react'

import { AriaLiveProvider } from 'js/components/AriaLive'
import ErrorBoundary from 'js/components/ErrorBoundary'
import client from 'js/lib/client'
import { GlobalStateProvider } from 'js/lib/GlobalState'
import SearchProvider from 'js/search/providers/SearchProvider'

import App from './App'
import insertDomElement from './lib/insertDomElement'

const container = document.querySelector('#react-root')

// Ensure modal-root is created before React rendering begins
insertDomElement({
    attr: { id: 'modal-root' },
    after: container,
})

Sentry.init({
    dsn: import.meta.env.SENTRY_DSN,
    enabled: import.meta.env.MODE !== 'development',
    tracesSampleRate: import.meta.env.SENTRY_TRACES_SAMPLE_RATE,
    // Sentry 11 collects more by default (cookies, request bodies, user info);
    // keep the restrictive Sentry 10 baseline.
    dataCollection: {
        userInfo: false,
        cookies: false,
        httpHeaders: {
            request: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
            response: { deny: ['forwarded', '-ip', 'remote-', 'via', '-user'] },
        },
        httpBodies: [],
        urlQueryParams: {
            deny: ['forwarded', '-ip', 'remote-', 'via', '-user'],
        },
        genAI: { inputs: false, outputs: false },
        databaseQueryData: false,
        queues: false,
        graphQL: { document: false, variables: false },
    },
    // Set at build time from the CI build scope, so it matches the names the
    // backend reports. MODE is "production" for every built image.
    environment: import.meta.env.SENTRY_ENVIRONMENT || import.meta.env.MODE,
    // Only report errors thrown from our own bundles (served from STATIC_URL on
    // the site's origin). This drops third-party scripts such as the Matomo
    // container and browser extensions. Events without a stacktrace still pass.
    allowUrls: [`${window.location.origin}/static/`],
    beforeSend(event) {
        if (event.user) {
            delete event.user.name
            delete event.user.email
            delete event.user.ip_address
        }
        return event
    },
})

const render = (Component: React.ComponentType) => {
    const root = createRoot(container!, {
        onUncaughtError: Sentry.reactErrorHandler((error, info) => {
            console.error(error, info.componentStack)
        }),
        onRecoverableError: Sentry.reactErrorHandler((error, info) => {
            console.error(error, info.componentStack)
        }),
    })
    root.render(
        <ErrorBoundary>
            <ApolloProvider client={client}>
                <GlobalStateProvider>
                    <AriaLiveProvider>
                        <SearchProvider>
                            <Component />
                        </SearchProvider>
                    </AriaLiveProvider>
                </GlobalStateProvider>
            </ApolloProvider>
        </ErrorBoundary>,
    )
}

render(App)
